import { useState, useEffect } from 'react';

/**
 * Hook to detect dark mode state and provide theme-aware colors
 */
export function useDarkMode() {
  const [isDark, setIsDark] = useState(() => {
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    // Watch for changes to the dark class on html element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDark(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

/**
 * Theme-aware color palette for charts
 */
export function useChartColors() {
  const isDark = useDarkMode();

  return {
    // Grid and axis colors
    gridColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    gridZeroLine: isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
    textColor: isDark ? '#e5e7eb' : '#374151',
    textMuted: isDark ? '#9ca3af' : '#6b7280',

    // Dataset colors - slightly adjusted for dark mode
    primary: {
      border: isDark ? 'rgb(96, 165, 250)' : 'rgb(59, 130, 246)',
      background: isDark ? 'rgba(96, 165, 250, 0.15)' : 'rgba(59, 130, 246, 0.1)',
    },
    success: {
      border: isDark ? 'rgb(74, 222, 128)' : 'rgb(34, 197, 94)',
      background: isDark ? 'rgba(74, 222, 128, 0.15)' : 'rgba(34, 197, 94, 0.1)',
    },
    warning: {
      border: isDark ? 'rgb(251, 191, 36)' : 'rgb(245, 158, 11)',
      background: isDark ? 'rgba(251, 191, 36, 0.15)' : 'rgba(245, 158, 11, 0.1)',
    },
    error: {
      border: isDark ? 'rgb(248, 113, 113)' : 'rgb(239, 68, 68)',
      background: isDark ? 'rgba(248, 113, 113, 0.15)' : 'rgba(239, 68, 68, 0.1)',
    },
    teal: {
      border: isDark ? 'rgb(94, 234, 212)' : 'rgb(20, 184, 166)',
      background: isDark ? 'rgba(94, 234, 212, 0.15)' : 'rgba(20, 184, 166, 0.1)',
    },
    pink: {
      border: isDark ? 'rgb(244, 114, 182)' : 'rgb(236, 72, 153)',
      background: isDark ? 'rgba(244, 114, 182, 0.15)' : 'rgba(236, 72, 153, 0.1)',
    },

    // Tooltip styling
    tooltip: {
      background: isDark ? '#1f2937' : '#ffffff',
      border: isDark ? '#374151' : '#e5e7eb',
      text: isDark ? '#f9fafb' : '#111827',
    },

    isDark,
  };
}
